import axios from 'axios';
import { io } from 'socket.io-client';

// Auto-detect API host:
// - localhost → direct backend port 5001
// - domain (banggiatva.top) → Nginx proxies /api and /socket.io
const isLocal = window.location.hostname === 'localhost';
const API_HOST = isLocal
  ? 'http://localhost:5001'
  : `${window.location.protocol}//${window.location.host}`;

const API_BASE_URL = `${API_HOST}/api`;
const SOCKET_URL = API_HOST;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration (but not for login endpoint)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url === '/login';
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (username, password) => {
    const response = await api.post('/login', { username, password });
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
};

export const stockService = {
  getMAOverview: async (signal) => {
    const response = await api.get('/stocks/ma-overview', { signal });
    return response.data;
  },

  getMarketIndices: async (signal) => {
    const response = await api.get('/market-indices', { signal });
    return response.data;
  },

  getMinRange: async (fromDate, toDate, signal) => {
    const params = { from: fromDate };
    if (toDate) params.to = toDate;
    const response = await api.get('/min-range', { params, signal });
    return response.data;
  },

  getWatchlist: async () => {
    const response = await api.get('/watchlist');
    return response.data;
  },

  addToWatchlist: async (symbol) => {
    const response = await api.post('/watchlist', { symbol });
    return response.data;
  },

  removeFromWatchlist: async (symbol) => {
    const response = await api.delete(`/watchlist/symbol/${symbol}`);
    return response.data;
  },

  getEarlySignals: async (signal) => {
    const response = await api.get('/early-signals', { signal });
    return response.data;
  },
};

export const userService = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  create: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  delete: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  changePassword: async (userId, newPassword) => {
    const response = await api.put(`/users/${userId}/password`, { new_password: newPassword });
    return response.data;
  },
};

export const sectorService = {
  getSectors: async () => {
    const response = await api.get('/sectors');
    return response.data;
  },

  createSector: async (name) => {
    const response = await api.post('/sectors', { name });
    return response.data;
  },

  deleteSector: async (sectorId) => {
    const response = await api.delete(`/sectors/${sectorId}`);
    return response.data;
  },

  addSymbol: async (sectorId, symbol) => {
    const response = await api.post(`/sectors/${sectorId}/symbols`, { symbol });
    return response.data;
  },

  removeSymbol: async (sectorId, symbol) => {
    const response = await api.delete(`/sectors/${sectorId}/symbols/${symbol}`);
    return response.data;
  },

  reorderSectors: async (sectorIds) => {
    const response = await api.put('/sectors/reorder', { sector_ids: sectorIds });
    return response.data;
  },
};

// Socket.IO service for real-time updates
let socketInstance = null;

export const socketService = {
  connect: () => {
    if (socketInstance && socketInstance.connected) {
      return socketInstance;
    }

    socketInstance = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketInstance.on('connect', () => {
      console.log('Socket.IO connected:', socketInstance.id);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Socket.IO disconnected:', reason);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
    });

    return socketInstance;
  },

  disconnect: () => {
    if (socketInstance) {
      socketInstance.disconnect();
      socketInstance = null;
    }
  },
};

export default api;
